import React, { useEffect, useState } from "react";
import { X, ArrowRight, Check } from "lucide-react";

const STORAGE_KEY = "dplus_newsletter_popup_dismissed";

export default function NewsletterPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (dismissed) return;
    const t = setTimeout(() => setOpen(true), 8000); // 8 seconds
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    setOpen(false);
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch (_) {
      // ignore
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitted(true);
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch (_) {
      // ignore
    }
    setTimeout(() => setOpen(false), 1800);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 animate-[fadeIn_0.25s_ease-out]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={close}
        aria-hidden="true"
      />

      {/* Card */}
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-[920px] grid md:grid-cols-2 overflow-hidden animate-[fadeIn_0.35s_ease-out]">
        {/* Close */}
        <button
          onClick={close}
          aria-label="Close"
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/90 hover:bg-white border border-neutral-200 flex items-center justify-center transition-colors"
        >
          <X className="w-5 h-5 text-[#1a1a1a]" />
        </button>

        {/* Left visual */}
        <div className="relative hidden md:block bg-[#EEF3FE]">
          <img
            src="https://customer-assets.emergentagent.com/job_custom-print-flow/artifacts/conewxgg_tech-people-trying-achieve-ambitious-sustainability-goals.jpg"
            alt="Let’s Print team — sustainable printing"
            className="w-full h-full object-cover"
          />
          <div className="absolute top-5 left-5 bg-[#2C66E3] text-white text-xs font-bold tracking-[0.18em] px-3.5 py-1.5 rounded-full">
            LET’S PRINT · 20% OFF
          </div>
        </div>

        {/* Right content */}
        <div className="p-7 md:p-10 flex flex-col justify-center">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-14 h-14 rounded-full bg-[#EEF3FE] flex items-center justify-center mx-auto mb-4">
                <Check className="w-7 h-7 text-[#2C66E3]" />
              </div>
              <h3 className="text-2xl font-black text-[#1a1a1a] mb-2 tracking-tight">You're in.</h3>
              <p className="text-neutral-600">Your 20% off code is on its way to your inbox.</p>
            </div>
          ) : (
            <>
              <p className="text-xs tracking-[0.2em] font-semibold text-[#2C66E3] mb-3">JOIN LET’S PRINT</p>
              <h2 className="text-3xl md:text-[34px] font-black text-[#1a1a1a] tracking-tight leading-[1.1] mb-4">
                Unlock 20% off your first Let’s Print order.
              </h2>
              <p className="text-[15px] text-neutral-700 leading-relaxed mb-6">
                Join our community for exclusive deals, expert business tips, and daily inspiration.
              </p>

              <form onSubmit={onSubmit} className="relative mb-4">
                <input
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="w-full pl-5 pr-14 py-3.5 rounded-full border-2 border-neutral-300 focus:border-[#2C66E3] outline-none text-[15px]"
                />
                <button
                  type="submit"
                  aria-label="Submit"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#2C66E3] hover:bg-[#1E54C4] text-white flex items-center justify-center transition-colors"
                >
                  <ArrowRight className="w-5 h-5" />
                </button>
              </form>

              <p className="text-xs text-neutral-500 leading-relaxed mb-5">
                Valid on first orders of <strong>$40+</strong>. Excludes shipping, gift cards, and offline catalog products.
              </p>

              <button
                onClick={close}
                className="text-sm text-neutral-500 hover:text-[#1a1a1a] underline underline-offset-4 self-start"
              >
                No thanks, I'll pay full price
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
